import { useEffect, useRef, useState } from 'react';
import api from '../../../../services/api';
import { useTopologyBase } from './topologyApi';

/**
 * Hook: puxa o grafo de topologia via /admin/topology/{empresa_id} e mantém um polling
 * curto de alarmes via /admin/topology/{empresa_id}/alarms a cada 10s.
 *
 * Retorna:
 *  - graph: objeto do payload (nodes, edges, issues, vlans_index, ...)
 *  - alarms: dict host_id (zabbix) -> alarm
 *  - loading, error
 *  - reload(): força rebuild síncrono (GET ?force=true)
 */
export default function useTopologyPoll({ alarmsIntervalMs = 10000, mapId = null } = {}) {
  const base = useTopologyBase();
  const [graph, setGraph] = useState(null);
  const [alarms, setAlarms] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const alarmsTimer = useRef(null);

  const fetchGraph = async (force = false) => {
    if (!base) return;
    setLoading(true);
    setError(null);
    try {
      const params = [];
      if (force) params.push('force=true');
      if (mapId) params.push(`map_id=${mapId}`);
      const qs = params.length ? `?${params.join('&')}` : '';
      const res = await api.get(`${base}${qs}`);
      setGraph(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Erro ao carregar topologia');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlarms = async () => {
    if (!base) return;
    try {
      const res = await api.get(`${base}/alarms`);
      const list = res.data?.alarms || [];
      const map = {};
      for (const a of list) {
        const k = String(a.host_id || '');
        if (!k) continue;
        if (!map[k] || Number(a.severity) > Number(map[k].severity)) {
          map[k] = a;
        }
      }
      setAlarms(map);
    } catch {
      // silencioso
    }
  };

  // Fase 1: carrega o grafo
  useEffect(() => {
    if (!base) {
      setGraph(null);
      setAlarms({});
      if (alarmsTimer.current) clearInterval(alarmsTimer.current);
      return;
    }
    fetchGraph(false);
    // eslint-disable-next-line
  }, [base, mapId]);

  // Fase 2: só inicia polling de alarmes DEPOIS do grafo estar EFETIVAMENTE construído
  useEffect(() => {
    if (!base || !graph || graph.needs_rebuild === true) {
      if (alarmsTimer.current) clearInterval(alarmsTimer.current);
      return;
    }
    fetchAlarms();
    if (alarmsTimer.current) clearInterval(alarmsTimer.current);
    alarmsTimer.current = setInterval(fetchAlarms, alarmsIntervalMs);
    return () => {
      if (alarmsTimer.current) clearInterval(alarmsTimer.current);
    };
    // eslint-disable-next-line
  }, [base, graph]);

  return {
    graph,
    alarms,
    loading,
    error,
    reload: () => fetchGraph(true),
  };
}
